import { useState } from "react";
import { useAtomValue } from "jotai";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { Button, Section } from "../components";
import { dateToShortTime, dayToStringShort } from "../lib";
import { recusAuthStateAtom } from "../vendor";
import {
  bookSession,
  listBookedSessions,
  listPaymentMethods,
  listUpcomingFreestyleSessions,
  PaymentMethod,
  recordRecentBooking,
  RecusAuthState,
  recusRecentBookingsAtom,
  Session,
  useSelectedRecusMemberId,
} from "./api";
import penguinMoney from "../penguin_money.gif";
import { NoCardModal } from "./BookButton";

const SUCCESS_DISPLAY_MS = 5000;
const DEFAULT_SHOWN = 2;

export function QuickBookRecus() {
  const authState = useAtomValue(recusAuthStateAtom);
  const [memberId] = useSelectedRecusMemberId();
  const queryClient = useQueryClient();

  const { data: sessions } = useQuery(
    ["upcomingSessions", "recus", authState?.userId],
    () => listUpcomingFreestyleSessions(authState!),
    { enabled: !!authState },
  );
  const { data: bookedData } = useQuery(
    ["bookedSessions", "recus", memberId],
    () => listBookedSessions(authState!, memberId!),
    { enabled: !!authState && !!memberId, refetchOnWindowFocus: true },
  );

  const recentBookings = useAtomValue(recusRecentBookingsAtom);
  const [expanded, setExpanded] = useState(false);
  const [justBooked, setJustBooked] = useState(false);

  const bookMutation = useMutation({
    mutationFn: async (args: { session: Session; pm: PaymentMethod }) => {
      if (!authState || !memberId) throw new Error("missing auth or member");
      return bookSession(
        authState,
        args.session.id,
        args.pm.stripePaymentMethodId,
        memberId,
      );
    },
    onSuccess: (result, args) => {
      recordRecentBooking(args.session.startsAt);
      queryClient.setQueryData(
        ["bookedSessions", "recus", memberId],
        result.bookedSessions,
      );
      queryClient.invalidateQueries(["upcomingSessions"]);
      setJustBooked(true);
      window.setTimeout(() => setJustBooked(false), SUCCESS_DISPLAY_MS);
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  if (!authState) return null;

  const bookedIds = new Set((bookedData ?? []).map((b) => b.sessionId));
  const now = new Date();
  const availableSessions = (sessions ?? [])
    .filter((s) => !bookedIds.has(s.id) && s.startsAt > now)
    .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());

  const nextByTime = new Map<string, Session>();
  for (const s of availableSessions) {
    const t = dateToShortTime(s.startsAt);
    if (!nextByTime.has(t)) nextByTime.set(t, s);
  }
  const byTimeOfDay = (a: string, b: string) =>
    minsOfDay(nextByTime.get(a)!.startsAt) -
    minsOfDay(nextByTime.get(b)!.startsAt);
  const allTimes = Array.from(nextByTime.keys()).sort(byTimeOfDay);

  if (allTimes.length === 0) return null;

  const preferred = rankByFrequency(recentBookings).filter((t) =>
    nextByTime.has(t),
  );
  const defaultTimes =
    preferred.length > 0
      ? preferred.slice(0, DEFAULT_SHOWN)
      : allTimes.slice(0, DEFAULT_SHOWN);
  const shownTimes = expanded ? allTimes : [...defaultTimes].sort(byTimeOfDay);

  return (
    <Section>
      <div className="flex flex-row items-center justify-between w-full mb-4">
        <h2 className="text-text-secondary text-lg font-light">quick book</h2>
        {allTimes.length > DEFAULT_SHOWN && (
          <button
            className="text-text-secondary text-sm ml-2 underline decoration-dotted"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "show less" : "show more!"}
          </button>
        )}
      </div>
      <div className="mb-2">
        {bookMutation.isLoading ? (
          <div className="flex flex-col items-center">
            <img
              className="w-20 h-20 opacity-80 rounded"
              src={penguinMoney}
              alt="booking"
            />
          </div>
        ) : justBooked ? (
          <div className="flex flex-col items-center">
            <div>successfully booked! 🎉</div>
          </div>
        ) : (
          <div className="w-full grid grid-cols-2 gap-4 max-w-sm">
            {shownTimes.map((time) => {
              const session = nextByTime.get(time)!;
              return (
                <QuickBookButton
                  key={time}
                  time={time}
                  session={session}
                  authState={authState}
                  onBook={(pm) => bookMutation.mutate({ session, pm })}
                />
              );
            })}
          </div>
        )}
      </div>
    </Section>
  );
}

type Stage = "idle" | "confirming";

function QuickBookButton(props: {
  time: string;
  session: Session;
  authState: RecusAuthState;
  onBook: (pm: PaymentMethod) => void;
}) {
  const [stage, setStage] = useState<Stage>("idle");
  const [checkingPm, setCheckingPm] = useState(false);
  const [showNoCardModal, setShowNoCardModal] = useState(false);

  const priceDollars = Math.round(props.session.priceCents / 100);
  const dayStr = dayToStringShort[props.session.startsAt.getDay()];

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (checkingPm) return;
    if (stage === "idle") {
      setStage("confirming");
      window.setTimeout(() => setStage("idle"), 6000);
      return;
    }
    setCheckingPm(true);
    try {
      const pms = await listPaymentMethods(props.authState);
      const primary = pms[0] ?? null;
      if (!primary) {
        setShowNoCardModal(true);
        setStage("idle");
        return;
      }
      setStage("idle");
      props.onBook(primary);
    } finally {
      setCheckingPm(false);
    }
  };

  let label: string;
  let textClass: string;
  if (checkingPm) {
    label = "loading...";
    textClass = "text-base";
  } else if (stage === "confirming") {
    label = `book ${dayStr}? ($${priceDollars})`;
    textClass = "text-sm";
  } else {
    label = props.time;
    textClass = "text-2xl";
  }

  return (
    <>
      <Button onClick={handleClick} textClass={textClass}>
        {label}
      </Button>
      {showNoCardModal && (
        <NoCardModal close={() => setShowNoCardModal(false)} />
      )}
    </>
  );
}

function minsOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function rankByFrequency(times: string[]): string[] {
  const freq = new Map<string, number>();
  const firstIdx = new Map<string, number>();
  times.forEach((t, i) => {
    freq.set(t, (freq.get(t) ?? 0) + 1);
    if (!firstIdx.has(t)) firstIdx.set(t, i);
  });
  return Array.from(freq.keys()).sort((a, b) => {
    const df = (freq.get(b) ?? 0) - (freq.get(a) ?? 0);
    if (df !== 0) return df;
    return (firstIdx.get(a) ?? 0) - (firstIdx.get(b) ?? 0);
  });
}
